/**
 * Servizio per l'invio di email di upgrade delle subscription
 */

/**
 * Notifica la pubblicazione di un nuovo catalogo via Firebase Function
 * @param {string} catalogId - ID del catalogo pubblicato
 * @param {string} licenseId - ID della licenza/subscription selezionata
 * @returns {Promise<Object>} - Risultato della notifica
 */
export const notifyNewCatalogPublished = async (catalogId, licenseId) => {
    try {
        // Usa la variabile d'ambiente per l'URL della Firebase Function
        const apiUrl = process.env.REACT_APP_NEW_CATALOG_PUBLISHED;

        // Prepara i dati secondo il formato specificato
        const notificationData = {
            catalogId: catalogId,
            licenseId: licenseId
        };

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(notificationData),
            signal: AbortSignal.timeout(30000) // Timeout di 30 secondi
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        return {
            success: true,
            message: 'Notification sent successfully',
            data: result
        };

    } catch (error) {
        console.error('❌ Error sending catalog publication notification:', error);

        // Non bloccare il flusso principale, ma logga l'errore
        return {
            success: false,
            message: error.message,
            error: error
        };
    }
};

/**
 * Invia una richiesta per una nuova subscription via API
 * @param {Object} userProfile - Profilo dell'utente
 * @param {string} subscriptionMessage - Messaggio dell'utente
 * @returns {Promise<Object>} - Risultato dell'invio
 */
export const sendSubscriptionRequestEmail = async (userProfile, subscriptionMessage) => {
    try {
        // Usa la variabile d'ambiente se disponibile, altrimenti usa un fallback temporaneo
        const apiUrl = process.env.REACT_APP_SUBSCRIPTION_REQUEST;

        // Prepara i dati per l'email secondo il formato specificato
        const emailData = {
            to: 'hello@spaarkly.it',
            from: userProfile.email,
            subject: 'New Subscription Request',
            description: subscriptionMessage,
            subscriptionDetails: `User: ${userProfile.firstName} ${userProfile.lastName}\nEmail: ${userProfile.email}\nCompany: ${userProfile.companyName || 'N/A'}\nRole: ${userProfile.role}`,
            subscriptionName: 'New Subscription'
        };

        // Modalità di test: simula l'invio dell'email invece di fare la chiamata API reale
        // TODO: Rimuovere questa simulazione quando la Cloud Function sarà pronta
        const isTestMode = !process.env.REACT_APP_SUBSCRIPTION_REQUEST;

        if (isTestMode) {
            // Simula un delay per l'invio
            await new Promise(resolve => setTimeout(resolve, 2000));

            return {
                success: true,
                message: 'Subscription request processed successfully (test mode)',
                data: emailData
            };
        }

        // Configurazione fetch ottimizzata per browser (funziona su Postman)
        const fetchOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(emailData)
        };

        // Aggiungi timeout per evitare hang
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Request timeout')), 30000); // 30 secondi
        });

        try {
            // Invia la richiesta con timeout
            const response = await Promise.race([
                fetch(apiUrl, fetchOptions),
                timeoutPromise
            ]);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('API error response:', errorText);
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }

            const result = await response.json();

            return { success: true, data: result };

        } catch (fetchError) {
            // Se è un errore CORS, passa alla modalità test come fallback
            if (fetchError.message.includes('Failed to fetch') ||
                fetchError.message.includes('CORS') ||
                fetchError.message.includes('blocked')) {

                // Simula un delay per l'invio
                await new Promise(resolve => setTimeout(resolve, 2000));

                return {
                    success: true,
                    message: 'Subscription request processed (CORS fallback mode - server needs CORS configuration)',
                    data: emailData,
                    fallbackMode: true
                };
            }

            // Re-throw altri errori
            throw fetchError;
        }
    } catch (error) {
        console.error('❌ Error sending subscription request:', error);

        // Gestione specifica per diversi tipi di errore
        if (error.message.includes('Failed to fetch')) {
            throw new Error('Network error: Unable to connect to the server. This might be a CORS configuration issue on the server side.');
        }

        if (error.message.includes('CORS')) {
            throw new Error('CORS error: The server needs to allow requests from this domain.');
        }

        if (error.message.includes('timeout')) {
            throw new Error('Request timeout: The server took too long to respond.');
        }

        // Errore generico
        throw new Error(`Subscription request failed: ${error.message}`);
    }
};

/**
 * Invia una richiesta di upgrade via email
 * @param {Object} subscriptionData - Dati della subscription
 * @param {Object} userProfile - Profilo dell'utente
 * @param {string} upgradeMessage - Messaggio dell'utente
 * @returns {Promise<Object>} - Risultato dell'invio
 */
export const sendUpgradeRequestEmail = async (subscriptionData, userProfile, upgradeMessage) => {
    try {
        // Usa la variabile d'ambiente se disponibile, altrimenti usa un fallback temporaneo
        const apiUrl = process.env.REACT_APP_SUBSCRIPTION_REQUEST;

        // Prepara i dati per l'email secondo il formato specificato
        const emailData = {
            to: 'hello@spaarkly.it',
            from: userProfile.email,
            subject: `Upgrade Subscription Request - ${subscriptionData.name}`,
            description: upgradeMessage,
            subscriptionDetails: `User: ${userProfile.firstName} ${userProfile.lastName}\nEmail: ${userProfile.email}\nCompany: ${userProfile.companyName || 'N/A'}\nRole: ${userProfile.role}`,
            subscriptionName: subscriptionData.name
        };

        // Modalità di test: simula l'invio dell'email invece di fare la chiamata API reale
        // TODO: Rimuovere questa simulazione quando la Cloud Function sarà pronta
        const isTestMode = !process.env.REACT_APP_SUBSCRIPTION_REQUEST;

        if (isTestMode) {
            // Simula un delay per l'invio
            await new Promise(resolve => setTimeout(resolve, 2000));

            return {
                success: true,
                message: 'Upgrade request processed successfully (test mode)',
                data: emailData
            };
        }

        // Modalità produzione: chiamata API reale

        // Configurazione fetch ottimizzata per browser (funziona su Postman)
        const fetchOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(emailData)
        };

        // Aggiungi timeout per evitare hang
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Request timeout')), 30000); // 30 secondi
        });

        try {
            // Invia la richiesta con timeout
            const response = await Promise.race([
                fetch(apiUrl, fetchOptions),
                timeoutPromise
            ]);

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }

            const result = await response.json();

            return { success: true, data: result };

        } catch (fetchError) {
            // Se è un errore CORS, passa alla modalità test come fallback
            if (fetchError.message.includes('Failed to fetch') ||
                fetchError.message.includes('CORS') ||
                fetchError.message.includes('blocked')) {

                // Simula un delay per l'invio
                await new Promise(resolve => setTimeout(resolve, 2000));

                return {
                    success: true,
                    message: 'Upgrade request processed (CORS fallback mode - server needs CORS configuration)',
                    data: emailData,
                    fallbackMode: true
                };
            }

            // Re-throw altri errori
            throw fetchError;
        }
    } catch (error) {
        console.error('❌ Error sending upgrade request:', error);

        // Gestione specifica per diversi tipi di errore
        if (error.message.includes('Failed to fetch')) {
            throw new Error('Network error: Unable to connect to the server. This might be a CORS configuration issue on the server side.');
        }

        if (error.message.includes('CORS')) {
            throw new Error('CORS error: The server needs to allow requests from this domain.');
        }

        if (error.message.includes('timeout')) {
            throw new Error('Request timeout: The server took too long to respond.');
        }

        // Errore generico
        throw new Error(`Upgrade request failed: ${error.message}`);
    }
};

/**
 * Ottiene le informazioni del profilo utente dal secure storage e dal database
 * @returns {Promise<Object>} - Profilo dell'utente
 */
export const getUserProfileForEmail = async () => {
    const { secureGetItem } = await import('../data/utils');
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('../data/base');

    try {
        // Ottieni le informazioni di base dal secure storage
        const email = secureGetItem('email');
        const firstName = secureGetItem('p_firstName') || secureGetItem('Name') || '';
        const lastName = secureGetItem('p_lastName') || '';
        const role = secureGetItem('role');
        const clientRef = secureGetItem('ref_cliente');

        // Ottieni informazioni aggiuntive dal database se necessario
        let companyName = '';
        if (clientRef) {
            try {
                const clientDoc = await getDoc(doc(db, 'Client', clientRef));
                if (clientDoc.exists()) {
                    companyName = clientDoc.data().companyName || '';
                }
            } catch (error) {
                console.error('Error fetching client data:', error);
            }
        }

        return {
            email: email,
            firstName: firstName,
            lastName: lastName,
            role: role,
            companyName: companyName
        };
    } catch (error) {
        console.error('Error getting user profile for email:', error);
        throw error;
    }
}; 